import React, { useState } from 'react';
import { 
    Activity, Search, CheckCircle2, AlertTriangle, XCircle, 
    FileText, X
} from 'lucide-react';
import Button from '../../../components/Button';
import Input from '../../../components/Input';

export const IntegrationLogsTab = ({ logs = [], isDarkMode, onRefresh }) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('ALL');
    const [selectedLog, setSelectedLog] = useState(null);

    const logsArray = Array.isArray(logs) ? logs : [];

    const filteredLogs = logsArray.filter(log => {
        if (!log) return false;
        
        // Filtro de status
        if (statusFilter !== 'ALL') {
            const st = String(log.status || '').toUpperCase();
            if (statusFilter === 'SUCESSO' && st !== 'SUCESSO') return false;
            if (statusFilter === 'FALHA' && st !== 'FALHA' && st !== 'ERRO') return false;
            if (statusFilter === 'PARCIAL' && st !== 'PARCIAL') return false;
        }

        // Filtro de busca textual
        if (!searchTerm || !searchTerm.trim()) return true;
        const s = searchTerm.toLowerCase().trim();
        const tipo = String(log.tipo || '').toLowerCase();
        const user = String(log.executadoPor || '').toLowerCase();
        const status = String(log.status || '').toLowerCase();
        const id = String(log.id || '').toLowerCase();
        
        return tipo.includes(s) || user.includes(s) || status.includes(s) || id.includes(s);
    }).sort((a, b) => {
        const getMs = (item) => {
            if (!item) return 0;
            if (item.dataHora) {
                const ms = new Date(item.dataHora).getTime();
                if (!isNaN(ms)) return ms;
            }
            if (item.timestamp) {
                const ms = new Date(item.timestamp).getTime();
                if (!isNaN(ms)) return ms;
            }
            return 0;
        };
        return getMs(b) - getMs(a);
    });

    const formatDataHora = (isoStr) => {
        if (!isoStr) return '-';
        try {
            const d = new Date(isoStr);
            if (!isNaN(d.getTime())) return d.toLocaleString('pt-BR');
        } catch {}
        return isoStr;
    };

    const getTipoLabel = (tipo) => {
        switch (tipo) {
            case 'EQUIPE_INTERNA_ACADS':
                return 'Equipe Interna (Acads)';
            case 'HEADCOUNT_CLIENTES':
                return 'Headcount (apEx Clientes)';
            default:
                return tipo || 'Sincronização API';
        }
    };

    const getStatusBadge = (status) => {
        const st = String(status || '').toUpperCase();
        if (st === 'SUCESSO') {
            return (
                <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] font-bold bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 border border-green-200 dark:border-green-800">
                    <CheckCircle2 size={12} />
                    SUCESSO
                </span>
            );
        }
        if (st === 'PARCIAL') {
            return (
                <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] font-bold bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400 border border-amber-200 dark:border-amber-800">
                    <AlertTriangle size={12} />
                    PARCIAL
                </span>
            );
        }
        return (
            <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] font-bold bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400 border border-red-200 dark:border-red-800">
                <XCircle size={12} />
                {st || 'FALHA'}
            </span>
        );
    };

    return (
        <div className="p-6 overflow-y-auto custom-scrollbar space-y-6">
            
            {/* Cabeçalho */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 border-b pb-4 dark:border-slate-700">
                <div>
                    <h3 className="font-bold text-lg flex items-center gap-2">
                        <Activity className="text-blue-500" size={22} />
                        Histórico & Monitoramento de Integrações
                    </h3>
                    <p className="text-xs opacity-70 mt-1">
                        Acompanhe em tempo real as execuções de sincronizações do Alterdata, Omie e rotinas agendadas, verificando métricas, duração e eventuais erros.
                    </p>
                </div>
                <div className="text-xs font-mono opacity-60">
                    Total de registros: <b>{filteredLogs.length}</b>
                </div>
            </div>

            {/* Barra de Filtros */}
            <div className="flex flex-col sm:flex-row gap-3 items-center justify-between">
                <div className="relative w-full sm:w-80">
                    <Search className={`absolute left-3 top-2.5 ${isDarkMode ? 'text-slate-500' : 'text-gray-400'}`} size={16} />
                    <Input
                        placeholder="Buscar por rotina, usuário ou status..."
                        value={searchTerm}
                        onChange={e => setSearchTerm(e.target.value)}
                        className={`pl-9 h-9 ${isDarkMode ? 'bg-slate-900 border-slate-700' : ''}`}
                    />
                </div>

                <div className="flex items-center gap-2 w-full sm:w-auto">
                    <span className="text-xs font-bold opacity-60 whitespace-nowrap">Status:</span>
                    <select
                        value={statusFilter}
                        onChange={e => setStatusFilter(e.target.value)}
                        className={`h-9 text-xs rounded-lg border px-3 font-semibold ${
                            isDarkMode ? 'bg-slate-900 border-slate-700 text-white' : 'bg-white border-gray-300 text-gray-800'
                        }`}
                    >
                        <option value="ALL">Todos os Status</option>
                        <option value="SUCESSO">Apenas Sucesso</option>
                        <option value="PARCIAL">Parcial</option>
                        <option value="FALHA">Falhas / Erros</option>
                    </select>
                </div>
            </div>

            {/* Tabela de Logs */}
            <div className="border rounded-2xl overflow-hidden shadow-sm">
                <table className="w-full text-xs text-left">
                    <thead className={`uppercase font-bold ${
                        isDarkMode ? 'bg-slate-900 text-slate-400 border-b border-slate-700' : 'bg-gray-100 text-gray-600 border-b border-gray-200'
                    }`}>
                        <tr>
                            <th className="p-3">Data e Hora</th>
                            <th className="p-3">Rotina Executada</th>
                            <th className="p-3">Status</th>
                            <th className="p-3">Duração</th>
                            <th className="p-3">Resultados / Métricas</th>
                            <th className="p-3">Executado Por</th>
                            <th className="p-3 text-center">Ação</th>
                        </tr>
                    </thead>
                    <tbody className={`divide-y ${
                        isDarkMode ? 'divide-slate-700 bg-slate-800' : 'divide-gray-100 bg-white'
                    }`}>
                        {filteredLogs.map((log) => (
                            <tr key={log.id} className={isDarkMode ? 'hover:bg-slate-700/40' : 'hover:bg-gray-50'}>
                                <td className="p-3 font-mono font-bold whitespace-nowrap">
                                    {formatDataHora(log.dataHora || log.timestamp)}
                                </td>
                                <td className="p-3 font-semibold">
                                    <span className="px-2 py-0.5 rounded bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 font-bold border border-indigo-200 dark:border-indigo-800 text-[11px]">
                                        {getTipoLabel(log.tipo)}
                                    </span>
                                </td>
                                <td className="p-3">
                                    {getStatusBadge(log.status)}
                                </td>
                                <td className="p-3 font-mono text-[11px] opacity-75">
                                    {log.duracaoMs ? `${(log.duracaoMs / 1000).toFixed(1)}s` : '-'}
                                </td>
                                <td className="p-3 font-mono text-[11px]">
                                    {log.tipo === 'EQUIPE_INTERNA_ACADS' && (
                                        <span>
                                            <b>{log.totalColaboradores ?? '-'}</b> colaboradores em <b>{log.totalAcadsVinculadas ?? '-'}</b> Acads
                                        </span>
                                    )}
                                    {log.tipo === 'HEADCOUNT_CLIENTES' && (
                                        <span>
                                            <b>{log.totalAtualizados ?? '-'}</b> clientes atualizados de <b>{log.totalClientesVerificados ?? '-'}</b>
                                        </span>
                                    )}
                                    {!['EQUIPE_INTERNA_ACADS', 'HEADCOUNT_CLIENTES'].includes(log.tipo) && (
                                        <span>{log.detalhes || log.mensagem || 'Concluído'}</span>
                                    )}
                                </td>
                                <td className="p-3 text-xs opacity-75 truncate max-w-[150px]">
                                    {log.executadoPor || 'Sistema'}
                                </td>
                                <td className="p-3 text-center">
                                    <button
                                        onClick={() => setSelectedLog(log)}
                                        className="text-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/30 p-1.5 rounded-lg transition-colors font-bold inline-flex items-center gap-1 text-[11px]"
                                        title="Ver Detalhes do Log"
                                    >
                                        <FileText size={14} />
                                        <span>Ver Detalhes</span>
                                    </button>
                                </td>
                            </tr>
                        ))}
                        {filteredLogs.length === 0 && (
                            <tr>
                                <td colSpan="7" className="p-8 text-center opacity-50 italic">
                                    Nenhum log de integração encontrado para os filtros selecionados.
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {/* Modal de Detalhes do Log Selecionado */}
            {selectedLog && (
                <div 
                    className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-fade-in"
                    onClick={() => setSelectedLog(null)}
                >
                    <div 
                        className={`rounded-2xl w-full max-w-2xl shadow-2xl flex flex-col max-h-[85vh] overflow-hidden border ${
                            isDarkMode ? 'bg-slate-800 text-white border-slate-700' : 'bg-white text-gray-800 border-gray-200'
                        }`}
                        onClick={e => e.stopPropagation()}
                    >
                        <div className={`p-4 border-b flex justify-between items-center ${
                            isDarkMode ? 'bg-slate-900 border-slate-700' : 'bg-slate-100 border-gray-200'
                        }`}>
                            <div>
                                <h4 className="font-bold text-sm flex items-center gap-2">
                                    <Activity size={16} className="text-blue-500" />
                                    Inspeção do Log de Execução
                                </h4>
                                <p className="text-[11px] font-mono opacity-60">ID: {selectedLog.id}</p>
                            </div>
                            <button onClick={() => setSelectedLog(null)}>
                                <X size={20} />
                            </button>
                        </div>

                        <div className="p-5 overflow-y-auto custom-scrollbar space-y-4">
                            <div className="grid grid-cols-2 gap-3 text-xs">
                                <div>
                                    <span className="block opacity-60 font-bold uppercase text-[10px]">Rotina</span>
                                    <span className="font-semibold">{getTipoLabel(selectedLog.tipo)}</span>
                                </div>
                                <div>
                                    <span className="block opacity-60 font-bold uppercase text-[10px]">Status</span>
                                    {getStatusBadge(selectedLog.status)}
                                </div>
                                <div>
                                    <span className="block opacity-60 font-bold uppercase text-[10px]">Data e Hora</span>
                                    <span className="font-mono">{formatDataHora(selectedLog.dataHora || selectedLog.timestamp)}</span>
                                </div>
                                <div>
                                    <span className="block opacity-60 font-bold uppercase text-[10px]">Duração</span>
                                    <span className="font-mono">{selectedLog.duracaoMs ? `${(selectedLog.duracaoMs / 1000).toFixed(2)}s` : '-'}</span>
                                </div>
                                <div>
                                    <span className="block opacity-60 font-bold uppercase text-[10px]">Executado Por</span>
                                    <span>{selectedLog.executadoPor || 'Sistema'}</span>
                                </div>
                            </div>

                            {/* Ocorrências / Erros */}
                            {selectedLog.erros?.length > 0 && (
                                <div className="p-3 rounded-xl border border-red-300 bg-red-50 dark:bg-red-900/20 dark:border-red-800 text-xs text-red-600 dark:text-red-400 space-y-1">
                                    <p className="font-bold flex items-center gap-1.5">
                                        <AlertTriangle size={14} />
                                        Ocorrências / Avisos ({selectedLog.erros.length}):
                                    </p>
                                    <ul className="list-disc pl-4 space-y-0.5 text-[11px] font-mono">
                                        {selectedLog.erros.map((err, i) => (
                                            <li key={i}>{typeof err === 'object' ? JSON.stringify(err) : String(err)}</li>
                                        ))}
                                    </ul>
                                </div>
                            )}

                            {/* Payload Completo JSON */}
                            <div>
                                <span className="block opacity-60 font-bold uppercase text-[10px] mb-1">Payload JSON</span>
                                <pre className="p-3 rounded-xl bg-slate-950 text-emerald-400 text-[11px] font-mono overflow-x-auto max-h-60 select-all custom-scrollbar">
                                    {JSON.stringify(selectedLog, null, 2)}
                                </pre>
                            </div>
                        </div>

                        <div className={`p-3 border-t flex justify-end ${
                            isDarkMode ? 'bg-slate-900 border-slate-700' : 'bg-slate-50 border-gray-200'
                        }`}>
                            <Button variant="secondary" onClick={() => setSelectedLog(null)} className="text-xs">
                                Fechar
                            </Button>
                        </div>
                    </div>
                </div>
            )}

        </div>
    );
};

export default IntegrationLogsTab;
